<template>
  <div class="aveine-referential-list">
    <v-row class="d-flex align-center mb-4">
      <v-col cols="12" md="5">
        <v-text-field
          v-model="search"
          :label="$t('intranet.referential.search')"
          prepend-inner-icon="mdi-magnify"
          clearable
          hide-details
          @input="debouncedFetch"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="4">
        <v-select
          v-model="groupFilter"
          :items="groupSuggestions"
          :label="groupLabel"
          clearable
          hide-details
          @change="fetch"
        ></v-select>
      </v-col>
      <v-col cols="12" md="3" class="d-flex justify-end">
        <v-btn depressed color="primary" :to="`/authenticated/${resource}/create`">
          {{$t('intranet.referential.button.add')}}
        </v-btn>
      </v-col>
    </v-row>

    <v-data-table
      :headers="headers"
      :items="items"
      :loading="loading"
      :server-items-length="total"
      :options.sync="options"
      :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
      class="elevation-1"
    >
      <template v-slot:item.image="{ item }">
        <img v-if="item.image" class="thumb my-2" :alt="displayName(item)" :src="item.image">
        <v-icon v-else color="grey lighten-1">mdi-image-off-outline</v-icon>
      </template>

      <template v-slot:item.name="{ item }">
        {{ displayName(item) }}
      </template>

      <template v-slot:item.actions="{ item }">
        <v-btn icon :to="`/authenticated/${resource}/update/${item.id}`">
          <v-icon>mdi-pencil</v-icon>
        </v-btn>
        <v-btn icon @click="askDelete(item)">
          <v-icon>mdi-delete</v-icon>
        </v-btn>
      </template>
    </v-data-table>

    <v-dialog v-model="deleteDialog" max-width="480">
      <v-card>
        <v-card-title class="text-h6">
          {{$t('intranet.referential.title.deleteConfirmation')}}
        </v-card-title>
        <v-card-text v-if="itemToDelete">
          {{ displayName(itemToDelete) }}
        </v-card-text>
        <v-card-actions class="d-flex justify-end">
          <v-btn text @click="deleteDialog = false">{{$t('intranet.referential.button.cancel')}}</v-btn>
          <v-btn depressed color="primary" @click="confirmDelete()">{{$t('intranet.referential.button.delete')}}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { errorHandler } from 'aveine-toolbox'

/**
 * Liste d'un referentiel de degustation : aromes ou accords mets-vins.
 *
 * Pagination cote serveur, comme le reste du back-office : le referentiel est
 * petit aujourd'hui (32 aromes, 18 accords) mais rien ne garantit qu'il le
 * reste, et une liste complete chargee d'un coup se degrade en silence.
 */
export default {
  name: 'AveineReferentialList',
  props: {
    model: { type: Function, required: true },
    resource: { type: String, required: true },
    entity: { type: String, required: true },
    groupField: { type: String, required: true },
    groupLabel: { type: String, required: true },
    APIbaseURL: String
  },
  data () {
    return {
      items: [],
      total: 0,
      loading: false,
      search: null,
      groupFilter: null,
      groupSuggestions: [],
      deleteDialog: false,
      itemToDelete: null,
      debounceHandle: null,
      options: { page: 1, itemsPerPage: 25 }
    }
  },
  computed: {
    headers () {
      return [
        { text: this.$t('intranet.referential.attributes.image'), value: 'image', sortable: false, width: 90 },
        { text: this.$t('intranet.referential.attributes.name'), value: 'name', sortable: false },
        { text: this.groupLabel, value: this.groupField, sortable: false },
        { text: '', value: 'actions', sortable: false, align: 'end', width: 120 }
      ]
    }
  },
  watch: {
    options: { handler () { this.fetch() }, deep: true }
  },
  async created () {
    await this.fetch()
  },
  methods: {
    /**
     * Le nom est un objet de traductions. On retombe sur l'anglais puis sur la
     * premiere valeur non vide : une fiche traduite dans une seule langue reste
     * lisible dans la liste au lieu d'afficher une case vide.
     */
    displayName (item) {
      const name = item.name || {}
      return name[this.$i18n.locale] || name.en || Object.values(name).find(v => v) || '—'
    },

    debouncedFetch () {
      clearTimeout(this.debounceHandle)
      this.debounceHandle = setTimeout(() => this.fetch(), 300)
    },

    async fetch () {
      this.loading = true
      try {
        const params = {
          'page[number]': this.options.page,
          'page[size]': this.options.itemsPerPage
        }
        // FIQL. `=ilike=` avec des `%`, et surtout PAS `=re=` : cet opérateur
        // n'existe pas dans le parseur de creddy — src/fiql/parser.cr ne connaît
        // que gt/ge/lt/le/like/unlike/ilike/unilike/between/in/out. Un opérateur
        // inconnu ne provoque pas d'erreur : l'API répond 200, pose un
        // « Query parameter `filter` could not be parsed » dans meta.warnings, et
        // IGNORE le filtre. La recherche renvoyait donc tout, en silence.
        //
        // `name` est une traduction : on cherche dans la langue affichée, celle
        // que la liste montre. Les jokers saisis par l'utilisateur sont échappés,
        // sans quoi un `%` élargirait la recherche au lieu de la restreindre.
        const filters = []
        if (this.search) {
          const term = String(this.search).replace(/[%_\\]/g, m => '\\' + m)
          filters.push(`name.${this.$i18n.locale}=ilike=%${term}%`)
        }
        if (this.groupFilter) filters.push(`${this.groupField}==${this.groupFilter}`)
        if (filters.length) params.filter = filters.join(';')

        const response = await this.model.api().get(
          `${this.resource}?lang=${this.$i18n.locale}`,
          { baseURL: this.APIbaseURL, params }
        )

        this.items = response.entities ? (response.entities[this.entity] || []) : []
        this.total = response.response.data.meta ? response.response.data.meta.total : this.items.length
        this.collectGroups()
      } catch (errors) {
        errorHandler(errors, this)
      } finally {
        this.loading = false
      }
    },

    /**
     * Les valeurs de regroupement sont deduites des elements affiches : il n'y
     * a pas de table de familles cote base, c'est une colonne texte libre.
     * La liste ne peut donc etre qu'indicative, jamais exhaustive.
     */
    collectGroups () {
      const seen = new Set(this.groupSuggestions)
      this.items.forEach(item => { if (item[this.groupField]) seen.add(item[this.groupField]) })
      this.groupSuggestions = [...seen].sort()
    },

    askDelete (item) {
      this.itemToDelete = item
      this.deleteDialog = true
    },

    async confirmDelete () {
      try {
        await this.model.api().delete(
          `${this.resource}/${this.itemToDelete.id}`,
          { baseURL: this.APIbaseURL, delete: this.itemToDelete.id }
        )
        this.$toastr.s(this.$t('intranet.referential.title.deleted'))
        this.deleteDialog = false
        this.itemToDelete = null
        await this.fetch()
      } catch (errors) {
        this.deleteDialog = false
        errorHandler(errors, this)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  .thumb {
    max-width: 56px;
    max-height: 56px;
    display: block;
  }
</style>
